/**
 * Negotiation-history diff helper (task #1680).
 *
 * The trip_suggestions table records every booking proposal as a full
 * snapshot of the trip terms — dates, times, location, fees, totals.
 * Renting that as "Guest proposal #N · Mar 4 — Mar 6 · $206.07" for every
 * row in the chain is noisy: the reader has to spot-the-difference between
 * adjacent entries themselves.
 *
 * This helper compares a proposal against the previous accepted state
 * (the seed row, or the most recent prior accepted row) and returns just
 * the fields that actually changed, with before/after values ready to
 * render. Both TripDetailPage (renter view) and OwnerBookingDetailPage
 * (host view) use this so the negotiation chain reads identically on
 * both sides.
 */

import type { TripAdjustment } from "./bookings-api";

export interface AdjustmentBaseline {
  start_date: string;
  end_date: string;
  pickup_at: string | null;
  dropoff_at: string | null;
  is_delivery: boolean;
  delivery_distance: number | null;
  airport_code: string | null;
  delivery_address: string | null;
  payment_due: number;
}

export interface DiffField {
  field: "dates" | "pickup_time" | "dropoff_time" | "location" | "total";
  before: string;
  after: string;
}

export function adjustmentToBaseline(a: TripAdjustment): AdjustmentBaseline {
  const isDelivery =
    !!a.is_delivery ||
    !!a.airport_code ||
    (a.delivery_distance != null && a.delivery_distance > 0);
  return {
    start_date: a.start_date,
    end_date: a.end_date,
    pickup_at: a.pickup_at ?? null,
    dropoff_at: a.dropoff_at ?? null,
    is_delivery: isDelivery,
    delivery_distance: a.delivery_distance ?? null,
    airport_code: a.airport_code ?? null,
    delivery_address: a.delivery_address ?? null,
    payment_due: Number(a.payment_due ?? 0),
  };
}

/**
 * Find the seed row of the negotiation chain — the earliest proposal with
 * `counter_of == null`. The server seeds this row at booking creation to
 * record the original terms; subsequent rows are real change requests.
 * Returns null when the list is empty.
 */
export function findSeedAdjustment(adjustments: TripAdjustment[]): TripAdjustment | null {
  if (adjustments.length === 0) return null;
  let seed: TripAdjustment | null = null;
  for (const a of adjustments) {
    if (a.counter_of != null) continue;
    if (!seed) { seed = a; continue; }
    if (new Date(a.created_at).getTime() < new Date(seed.created_at).getTime()) seed = a;
  }
  return seed;
}

export interface DiffOptions {
  /** Render an ISO `YYYY-MM-DD` (or full ISO) as a short date — e.g. "Jun 10". */
  formatDate: (iso: string) => string;
  /** Render an ISO timestamp as a time only — e.g. "10:00 AM". Empty string when null. */
  formatTime: (iso: string | null) => string;
  /** Render a money amount as a currency string — e.g. "$206.07". */
  formatTotal: (amount: number) => string;
  /** Render a baseline's location as a single human string for diff comparison. */
  formatLocation: (b: AdjustmentBaseline) => string;
}

/**
 * Compare two baselines and return only the fields that materially differ.
 * Total diffs ignore sub-cent rounding noise. Times default to "" so a
 * baseline that never captured times doesn't read as a "change to —".
 */
export function diffAdjustment(
  prev: AdjustmentBaseline,
  next: AdjustmentBaseline,
  opts: DiffOptions,
): DiffField[] {
  const diffs: DiffField[] = [];

  if (prev.start_date !== next.start_date || prev.end_date !== next.end_date) {
    diffs.push({
      field: "dates",
      before: `${opts.formatDate(prev.start_date)} – ${opts.formatDate(prev.end_date)}`,
      after: `${opts.formatDate(next.start_date)} – ${opts.formatDate(next.end_date)}`,
    });
  }

  const prevPickup = opts.formatTime(prev.pickup_at);
  const nextPickup = opts.formatTime(next.pickup_at);
  if (prevPickup !== nextPickup) {
    diffs.push({ field: "pickup_time", before: prevPickup || "—", after: nextPickup || "—" });
  }

  const prevDropoff = opts.formatTime(prev.dropoff_at);
  const nextDropoff = opts.formatTime(next.dropoff_at);
  if (prevDropoff !== nextDropoff) {
    diffs.push({ field: "dropoff_time", before: prevDropoff || "—", after: nextDropoff || "—" });
  }

  const prevLoc = opts.formatLocation(prev);
  const nextLoc = opts.formatLocation(next);
  if (prevLoc !== nextLoc) {
    diffs.push({ field: "location", before: prevLoc, after: nextLoc });
  }

  if (Math.abs(prev.payment_due - next.payment_due) >= 0.01) {
    diffs.push({
      field: "total",
      before: opts.formatTotal(prev.payment_due),
      after: opts.formatTotal(next.payment_due),
    });
  }

  return diffs;
}

/**
 * For a row at index `i` in a chronologically-sorted (oldest → newest)
 * adjustments list, return the baseline to diff against — the most recent
 * earlier row whose status was accepted, falling back to the seed row at
 * index 0 (the booking's original terms) when nothing earlier was
 * explicitly accepted yet.
 */
export function previousBaseline(rowsAsc: TripAdjustment[], i: number): AdjustmentBaseline {
  for (let j = i - 1; j >= 0; j--) {
    if (rowsAsc[j].status === "accepted") return adjustmentToBaseline(rowsAsc[j]);
  }
  return adjustmentToBaseline(rowsAsc[0]);
}
